import { useCallback, useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { PagedQueryOptions } from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import {
  CATALOG_TABLE_ASSETS,
  CATALOG_TABLE_ASSETS_FILTER_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import { PAGE_SIZE } from '@elementary/common-ui/constants';
import { CATALOG_TABLE_GROUP_BY } from '@elementary/common-ui/constants/urlParams';
import { parseGraphqlFilterOptions } from '@elementary/common-ui/utils/graphqlFilters';
import useGroupByParam from 'shared/hooks/useGroupByParam';
import useGroupedTableData from 'shared/hooks/useGroupedTableData';
import { formatGroupTitle, getGroupValueFilter } from 'shared/utils/utils';
import {
  CatalogAssetsFilterOptionsResponse,
  CatalogTableAssetPagedResponse,
  getCatalogAssetsFilterOptionsQuery,
  getCatalogTableAssetsQuery,
} from '../api';
import {
  COLUMN_TO_FIELD_MAP,
  FIELD_TO_COLUMN_MAP,
  GROUP_TITLE_FORMATTERS,
  GROUPABLE_COLUMN_IDS,
  HIDDEN_CHILD_TYPES_FILTER,
} from '../consts';
import { CatalogTableAssetResponseItem } from '../types';
import { parseAssets } from '../utils';
import { useCatalogTableQueryContext } from './useCatalogTableQueryContext';

type Props = {
  search?: string;
};

export const useGroupedCatalogTableData = ({ search }: Props) => {
  const { groupBy, onGroupByChange } = useGroupByParam({
    paramKey: CATALOG_TABLE_GROUP_BY,
    groupableColumnIds: GROUPABLE_COLUMN_IDS,
  });
  const isGrouped = !!groupBy;

  const {
    selectedEnvId,
    selectedFolderAssetIds,
    selectedFolderAssetIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    urlSort,
    assetFilters,
    sort,
    filterOptions,
  } = useCatalogTableQueryContext();

  const groupEnabled = isGrouped && hasSelectedNodes;
  const field = groupBy ? COLUMN_TO_FIELD_MAP[groupBy] : undefined;
  const assetIds = selectedFolderAssetIds?.length
    ? selectedFolderAssetIds
    : null;
  const trimmedSearch = search?.trim() || null;

  const groupableColumnIds = useMemo(
    () =>
      GROUPABLE_COLUMN_IDS.filter(
        (columnId) => (filterOptions[columnId]?.length ?? 0) > 0,
      ),
    [filterOptions],
  );

  const groupAssetFilters = useMemo(
    () => [...assetFilters, HIDDEN_CHILD_TYPES_FILTER],
    [assetFilters],
  );

  const { data: groupOptionsData, isFetching: isGroupOptionsFetching } =
    useGraphqlQuery<CatalogAssetsFilterOptionsResponse>({
      queryKey: [
        CATALOG_TABLE_ASSETS_FILTER_OPTIONS,
        selectedEnvId,
        selectedFolderAssetIdsHash,
        'grouped',
        urlFiltersDebounced,
        trimmedSearch,
      ],
      query: getCatalogAssetsFilterOptionsQuery,
      variables: {
        filters: { ids: assetIds, search: trimmedSearch },
        assetFilters: groupAssetFilters,
      },
      enabled: groupEnabled,
      envId: selectedEnvId,
      keepPreviousData: true,
    });

  const groupValues = useMemo(() => {
    if (!groupBy) return [];
    const parsed = parseGraphqlFilterOptions(
      groupOptionsData?.assetsFilterOptions || [],
      FIELD_TO_COLUMN_MAP,
    );
    return parsed[groupBy] ?? [];
  }, [groupOptionsData, groupBy]);

  const nonEmptyGroupValues = useMemo(
    () => groupValues.map(({ value }) => value).filter(Boolean),
    [groupValues],
  );

  const formatTitle = useCallback(
    (value: string) =>
      groupBy
        ? formatGroupTitle(GROUP_TITLE_FORMATTERS, groupBy, value)
        : value,
    [groupBy],
  );

  const buildQueryOptions = useCallback(
    (
      value: string,
    ): PagedQueryOptions<
      CatalogTableAssetResponseItem,
      CatalogTableAssetPagedResponse
    > => {
      const groupFilter = field
        ? getGroupValueFilter(field, value, nonEmptyGroupValues)
        : undefined;
      return {
        queryKey: [
          CATALOG_TABLE_ASSETS,
          selectedEnvId,
          selectedFolderAssetIdsHash,
          urlFiltersDebounced,
          urlSort,
          trimmedSearch,
          groupFilter,
        ],
        query: getCatalogTableAssetsQuery,
        variables: {
          assetIds,
          search: trimmedSearch,
          assetFilters: groupFilter
            ? [...groupAssetFilters, groupFilter]
            : null,
          sort,
          limit: PAGE_SIZE,
        },
        select: (data) => data.assetsPaged,
        enabled: groupEnabled && !!groupFilter,
        envId: selectedEnvId,
      };
    },
    [
      field,
      nonEmptyGroupValues,
      selectedEnvId,
      selectedFolderAssetIdsHash,
      urlFiltersDebounced,
      urlSort,
      trimmedSearch,
      assetIds,
      groupAssetFilters,
      sort,
      groupEnabled,
    ],
  );

  const {
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    isAnyFetchingWithRows,
  } = useGroupedTableData({
    groupBy,
    groupValues,
    enabled: !!field,
    formatGroupTitle: formatTitle,
    buildQueryOptions,
    mapItems: parseAssets,
  });

  const data = useMemo(
    () => groupedData.flatMap((group) => group.data),
    [groupedData],
  );

  const hasResults = groupedData.some((group) => (group.count ?? 0) > 0);

  return {
    groupBy,
    onGroupByChange,
    isGrouped,
    groupableColumnIds,
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    data,
    hasResults,
    isFetching: isGroupOptionsFetching || isAnyFetchingWithRows,
  };
};
